import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { skills } from "../../data/constants";
import { useTheme } from "styled-components";

// Subtle animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const gentlePulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 2px 0; /* Reduced from 80px */
  background: ${({ theme }) => theme.bg};
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 15px; /* Reduced from 20px */
  padding: 0 20px;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

export const Title = styled.div`
  font-size: 38px; /* Slightly reduced */
  text-align: center;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  position: relative;
  padding-bottom: 12px; /* Reduced from 15px */

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 70px; /* Reduced from 80px */
    height: 3px; /* Reduced from 4px */
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.primary},
      ${({ theme }) => theme.primary}80
    );
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 28px; /* Reduced from 32px */
  }
`;

export const Desc = styled.div`
  font-size: 16px; /* Reduced from 18px */
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
  margin-bottom: 5px; /* Reduced from 10px */

  @media (max-width: 768px) {
    font-size: 14px; /* Reduced from 16px */
  }
`;

const SkillsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: 25px; /* Reduced from 40px */
  gap: 25px; /* Reduced from 30px */
  justify-content: center;
  animation: ${fadeIn} 0.8s ease;

  @media (max-width: 768px) {
    gap: 18px; /* Reduced from 20px */
    margin-top: 20px; /* Reduced */
  }
`;

const Skill = styled.div`
  width: 100%;
  max-width: 500px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.primary}20;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 18px; /* Slightly reduced */
  padding: 24px 32px; /* Reduced from 28px 36px */
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px; /* Reduced from 5px */
    height: 0;
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.primary},
      ${({ theme }) => theme.primary}80
    );
    transition: height 0.5s ease;
  }

  &:hover {
    transform: translateY(-4px); /* Reduced from 5px */
    box-shadow: 0 10px 35px rgba(133, 76, 230, 0.15); /* Slightly reduced */

    &:before {
      height: 100%;
    }
  }

  @media (max-width: 768px) {
    max-width: 400px;
    padding: 18px 24px; /* Reduced */
  }

  @media (max-width: 500px) {
    max-width: 330px;
    padding: 16px 20px; /* Reduced */
  }
`;

const SkillTitle = styled.h2`
  font-size: 24px; /* Reduced from 26px */
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 20px; /* Reduced from 24px */
  text-align: center;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  padding: 0 10px; /* Reduced from 20px */

  &:after {
    content: "";
    position: absolute;
    bottom: -6px; /* Reduced from -8px */
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.primary},
      transparent
    );
    border-radius: 2px;
  }
`;

const SkillList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px; /* Reduced from 14px */
  margin-bottom: 5px; /* Reduced from 10px */
`;

const SkillItem = styled.div`
  font-size: 14px; /* Reduced from 15px */
  font-weight: 500;
  color: ${({ theme }) => theme.text_primary};
  background: ${({ theme }) =>
    `rgba(${theme.primary === "#854CE6" ? "133, 76, 230" : "0, 0, 0"}, 0.08)`};
  border: 1px solid ${({ theme }) => theme.primary}20;
  border-radius: 12px; /* Reduced from 14px */
  padding: 10px 16px; /* Reduced from 12px 18px */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px; /* Reduced from 10px */
  transition: all 0.3s ease;
  cursor: default;
  backdrop-filter: blur(10px);

  &:hover {
    background: ${({ theme }) =>
      `rgba(${
        theme.primary === "#854CE6" ? "133, 76, 230" : "0, 0, 0"
      }, 0.12)`};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(133, 76, 230, 0.1); /* Reduced from 5px 15px */
    border-color: ${({ theme }) => theme.primary}40;
  }

  @media (max-width: 768px) {
    font-size: 13px; /* Reduced from 14px */
    padding: 8px 12px; /* Reduced */
    gap: 6px; /* Reduced from 8px */
  }

  @media (max-width: 500px) {
    font-size: 12px; /* Reduced from 13px */
    padding: 7px 10px; /* Reduced */
    border-radius: 10px; /* Reduced from 12px */
  }
`;

const SkillImage = styled.img`
  width: 24px; /* Reduced from 26px */
  height: 24px; /* Reduced from 26px */
  object-fit: contain;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));

  ${SkillItem}:hover & {
    animation: ${float} 3s ease-in-out infinite;
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 20px; /* Reduced from 22px */
    height: 20px; /* Reduced from 22px */
  }
`;

const DecorativeDots = styled.div`
  position: absolute;
  top: 15px; /* Reduced from 20px */
  right: 15px; /* Reduced from 20px */
  width: 50px; /* Reduced from 60px */
  height: 50px; /* Reduced from 60px */
  opacity: 0.1;
  background-image: radial-gradient(
    ${({ theme }) => theme.primary} 1.5px,
    transparent 2px
  );
  background-size: 10px 10px;

  @media (max-width: 768px) {
    width: 35px; /* Reduced from 40px */
    height: 35px; /* Reduced from 40px */
  }
`;

const Skills = () => {
  const theme = useTheme();
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container id="skills">
      <Wrapper>
        <Title>Skills</Title>
        <Desc>
          Here are some of my skills on which I have been working on for the
          past few years.
        </Desc>
        <SkillsContainer style={{ opacity: animated ? 1 : 0 }}>
          {skills.map((skill, index) => (
            <Skill key={index}>
              <DecorativeDots />
              <SkillTitle>{skill.title}</SkillTitle>
              <SkillList>
                {skill.skills.map((item, itemIndex) => (
                  <SkillItem key={itemIndex}>
                    <SkillImage src={item.image} alt={item.name} />
                    {item.name}
                  </SkillItem>
                ))}
              </SkillList>
            </Skill>
          ))}
        </SkillsContainer>
      </Wrapper>
    </Container>
  );
};

export default Skills;